import { useCallback, useEffect, useState } from "react";
import { CheckCircle, Gift, Loader, XCircle } from "lucide-react";
import { apiFetch } from "./api";

/**
 * Admin control: grant or revoke free in-app bookings for a practitioner, so launch partners
 * and house practitioners don't have to pay the $15/mo Booking subscription.
 */
export const PractitionerCompControl = ({ vendorId }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expires, setExpires] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(() => {
    apiFetch(`/admin/practitioner-comp/${vendorId}`, { skipVendorId: true })
      .then(setState)
      .catch(e => setError(e.message));
  }, [vendorId]);

  useEffect(() => { load(); }, [load]);

  const act = async (method) => {
    setBusy(true);
    setError("");
    try {
      const next = await apiFetch(`/admin/practitioner-comp/${vendorId}`, {
        method,
        skipVendorId: true,
        ...(method === "POST"
          ? { body: JSON.stringify({ expires_at: expires || null }) }
          : {}),
      });
      setState(next);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const label = () => {
    if (!state) return "Checking…";
    if (state.exempt) return "Included — admin account, never charged";
    if (state.comped) {
      return state.comp_expires_at
        ? `Comped until ${new Date(state.comp_expires_at).toLocaleDateString()}`
        : "Comped — free in-app bookings, no end date";
    }
    if (state.active) return "Paying — active Stripe subscription";
    return "No booking subscription — cannot take in-app bookings";
  };

  return (
    <div className="adm-detail-field" style={{ gridColumn: "1 / -1" }} data-testid={`practitioner-comp-${vendorId}`}>
      <label>Booking subscription</label>
      <p style={{ display: "flex", alignItems: "center", gap: 6 }} data-testid={`practitioner-comp-state-${vendorId}`}>
        {state?.comped || state?.exempt || state?.active
          ? <CheckCircle size={12} color="var(--green)" />
          : <XCircle size={12} color="var(--red)" />}
        {label()}
      </p>
      {error && (
        <p style={{ color: "var(--red)", fontSize: 12 }} data-testid={`practitioner-comp-error-${vendorId}`}>
          {error}
        </p>
      )}
      {state && !state.exempt && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          {state.comped ? (
            <button
              className="adm-btn-suspend"
              disabled={busy}
              onClick={() => act("DELETE")}
              data-testid={`practitioner-comp-revoke-${vendorId}`}
            >
              {busy ? <Loader size={14} /> : <XCircle size={14} />} Remove comp
            </button>
          ) : (
            <>
              <input
                type="date"
                value={expires}
                onChange={e => setExpires(e.target.value)}
                title="Optional end date — leave blank for no end date"
                style={{
                  padding: "7px 10px", borderRadius: 8, border: "1px solid var(--border)",
                  fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif",
                }}
                data-testid={`practitioner-comp-expires-${vendorId}`}
              />
              <button
                className="adm-btn-approve"
                disabled={busy}
                onClick={() => act("POST")}
                data-testid={`practitioner-comp-grant-${vendorId}`}
              >
                {busy ? <Loader size={14} /> : <Gift size={14} />} Comp booking subscription
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PractitionerCompControl;
